<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="program ? $t('loyalty.title.edit') : $t('loyalty.title.create')"
    width="900px"
    append-to-body
    custom-class="loyalty-form-dialog">
    <div class="px-6 py-4">
      <el-form :model="form" :rules="rules" ref="programForm" label-width="200px" class="loyalty-form">
        <!-- Name -->
        <el-form-item :label="$t('loyalty.label.name')" prop="name" class="mb-6">
          <el-input
            v-model="form.name"
            maxlength="255"
            :placeholder="$t('loyalty.placeholder.name')"
            class="w-full">
          </el-input>
        </el-form-item>

        <!-- Store (superadmin only) -->
        <el-form-item v-if="isSuperAdmin" :label="$t('loyalty.label.store')" prop="store_id" class="mb-6">
          <el-select
            v-model="form.store_id"
            :placeholder="$t('loyalty.label.store')"
            filterable
            class="w-full">
            <el-option
              v-for="store in stores"
              :key="store.id"
              :label="store.name"
              :value="store.id">
            </el-option>
          </el-select>
        </el-form-item>

        <!-- Description -->
        <el-form-item :label="$t('loyalty.label.description')" prop="description" class="mb-6">
          <el-input
            type="textarea"
            v-model="form.description"
            :rows="3"
            :placeholder="$t('loyalty.placeholder.description')"
            class="w-full">
          </el-input>
        </el-form-item>

        <!-- Points and Currency -->
        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.points_per_currency')" prop="points_per_currency">
            <el-input-number
              v-model="form.points_per_currency"
              :min="0"
              :precision="4"
              class="w-full">
            </el-input-number>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.currency_code')" prop="currency_code">
            <el-input
              v-model="form.currency_code"
              maxlength="3"
              :placeholder="$t('loyalty.placeholder.currency_code')"
              class="w-full">
            </el-input>
          </el-form-item>
        </div>

        <!-- Expiry Days -->
        <el-form-item :label="$t('loyalty.label.expiry_days')" prop="points_expiry_days" class="mb-6">
          <el-input-number
            v-model="form.points_expiry_days"
            :min="1"
            class="w-full">
          </el-input-number>
          <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.expiry_days') }}</p>
        </el-form-item>

        <!-- Auto Tier Settings -->
        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.auto_tier_upgrade')" prop="auto_tier_upgrade">
            <el-switch v-model="form.auto_tier_upgrade"></el-switch>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.auto_tier_upgrade') }}</p>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.auto_tier_downgrade')" prop="auto_tier_downgrade">
            <el-switch v-model="form.auto_tier_downgrade"></el-switch>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.auto_tier_downgrade') }}</p>
          </el-form-item>
        </div>

        <!-- Active Status -->
        <el-form-item :label="$t('loyalty.label.is_active')" prop="is_active" class="mb-6">
          <el-switch v-model="form.is_active"></el-switch>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="save"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('button.save') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'LoyaltyProgramForm',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    program: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      saving: false,
      stores: [],
      form: {
        name: '',
        description: '',
        store_id: null,
        points_per_currency: 1.0,
        currency_code: 'XAF',
        points_expiry_days: null,
        is_active: true,
        auto_tier_upgrade: true,
        auto_tier_downgrade: true,
        settings: {}
      }
    }
  },
  computed: {
    isSuperAdmin () {
      const user = this.$store.getters['Auth/getUser']
      return !!(user && user.is_superadmin)
    },
    rules () {
      return {
        name: [
          { required: true, message: this.$t('loyalty.validation.name_required'), trigger: 'blur' },
          { max: 255, message: 'Name cannot exceed 255 characters', trigger: 'blur' }
        ],
        store_id: this.isSuperAdmin
          ? [{ required: true, message: this.$t('loyalty.validation.store_required'), trigger: 'change' }]
          : [],
        points_per_currency: [
          { required: true, message: this.$t('loyalty.validation.points_per_currency_required'), trigger: 'blur' },
          { type: 'number', min: 0, message: this.$t('loyalty.validation.points_per_currency_positive'), trigger: 'blur' }
        ],
        currency_code: [
          { max: 3, message: 'Currency code must be exactly 3 characters', trigger: 'blur' },
          { required: true, message: this.$t('loyalty.validation.currency_code_required'), trigger: 'blur' }
        ],
        points_expiry_days: [
          { type: 'number', min: 1, message: 'Must be at least 1 day', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    program: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.form = {
            name: newVal.name || '',
            description: newVal.description || '',
            store_id: newVal.store_id || null,
            points_per_currency: parseFloat(newVal.points_per_currency) || 1.0,
            currency_code: newVal.currency_code || 'XAF',
            points_expiry_days: newVal.points_expiry_days || null,
            is_active: newVal.is_active !== undefined ? newVal.is_active : true,
            auto_tier_upgrade: newVal.auto_tier_upgrade !== undefined ? newVal.auto_tier_upgrade : true,
            auto_tier_downgrade: newVal.auto_tier_downgrade !== undefined ? newVal.auto_tier_downgrade : true,
            settings: newVal.settings || {}
          }
        } else {
          this.resetForm()
        }
      }
    },
    visible (newVal) {
      if (newVal && this.$refs.programForm) {
        this.$refs.programForm.clearValidate()
      }
    }
  },
  async mounted () {
    if (this.isSuperAdmin) {
      await this.loadStores()
    }
  },
  methods: {
    async loadStores () {
      try {
        this.stores = await this.$departmentService.getDepartments() || []
      } catch (error) {
        console.error('Error fetching stores:', error)
      }
    },
    resetForm () {
      this.form = {
        name: '',
        description: '',
        store_id: null,
        points_per_currency: 1.0,
        currency_code: 'XAF',
        points_expiry_days: null,
        is_active: true,
        auto_tier_upgrade: true,
        auto_tier_downgrade: true,
        settings: {}
      }
    },
    async save () {
      this.$refs.programForm.validate(async (valid) => {
        if (!valid) return

        try {
          this.saving = true
          let result
          if (this.program) {
            result = await this.$store.dispatch('loyalty/programs/updateProgram', {
              id: this.program.id,
              data: this.form
            })
            this.showSuccess(this.$t('loyalty.message.updated'))
          } else {
            result = await this.$store.dispatch('loyalty/programs/createProgram', this.form)
            this.showSuccess(this.$t('loyalty.message.created'))
          }
          this.$emit('saved', result)
        } catch (error) {
          console.error('Error saving program:', error)
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.loyalty-form-dialog ::v-deep .el-dialog__body {
  padding: 0;
}

.loyalty-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.loyalty-form ::v-deep .el-input__inner,
.loyalty-form ::v-deep .el-textarea__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.loyalty-form ::v-deep .el-input__inner:focus,
.loyalty-form ::v-deep .el-textarea__inner:focus {
  border-color: #3b82f6;
}
</style>
